import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MuiCheckbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import MUITableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import Typography from '@mui/material/Typography'
import { pathOr } from 'ramda'
import React from 'react'

import { type SchemaContact, type SchemaGroup } from '@/__generated/es-api'
import Loader from '@/app/component/atom/loader'
import SearchBar from '@/app/component/atom/search'
import Tooltip from '@/app/component/atom/tooltip'
import FieldStatic from '@/app/component/layout/field/static'
import IconText from '@/app/component/layout/icon-text'
import TableLayout from '@/app/component/layout/table'
import TableCell from '@/app/component/layout/table/cell'
import TableRow from '@/app/component/layout/table/row'
import TableToolbar from '@/app/component/layout/table/toolbar'
import { useIsMobile } from '@/app/hooks/use-is-mobile'
import { SELECT_CONTACTS_INFO_TOOLTIP } from '@/app/module/campaigns/definitions'
import { type CampaignItem } from '@/app/module/campaigns/types'
import { type SubscriptionFilter } from '@/app/module/campaigns/types/subscriptions'
import { type TableHeader, type TablePagination, type TableSpecs } from '@/app/types'

import ContactActions from './contact-actions'
import ContactsTableRow from './contacts-table-row'
import GroupActions from './group-actions'
import { MobileContactSubCard, MobileGroupSubCard, MobileSegmentSubCard } from './mobile-sub-cards'
import MobileSubscriptionsLayout from './mobile-subscriptions-layout'
import ResponsiveSubFilter from './responsive-sub-filter'

type CommonProps = {
  isLoading: boolean
  pagination: TablePagination
  refreshDisabled: boolean
  searchTerm?: string

  onClearSelection: () => void
  onRefresh: () => void
  onSearch: (searchTerm: string) => void
}

type ResponsiveContactSubTableProps = CommonProps & {
  campaign: CampaignItem
  contacts: SchemaContact[]
  resumableSubIds: number[]
  selectedContactIds: number[]
  selectedContactsById: Record<number, SchemaContact>
  selectionDisabledReason: string
  subscriptionFilter: SubscriptionFilter

  onResume: (subscriptionId: number) => Promise<void>
  onSelectContact: (contactId: number, checked: boolean) => void
  onSubscribe: (contactIds: number[]) => Promise<void>
  onSubscriptionFilterChange: (filter: SubscriptionFilter) => void
  onUnsubscribe: (contactIds: number[]) => Promise<void>
}

type ResponsiveGroupSegmentSubTableProps = CommonProps & {
  campaign: CampaignItem
  groups: SchemaGroup[]
  processingGroupIds: number[]
  root: 'groups' | 'segments'
  selectedGroupIds: number[]
  selectedGroupsById: Record<number, SchemaGroup>
  selectionDisabledReason: string
  timezone: string

  onSelectGroup: (groupId: number, checked: boolean) => void
  onSubscribe: (groupIds: number[]) => Promise<void>
  onUnsubscribe: (groupIds: number[]) => Promise<void>
}

type ResponsiveGroupSubTableProps = Omit<ResponsiveGroupSegmentSubTableProps, 'root'>

const ResponsiveContactSubTable: React.FC<ResponsiveContactSubTableProps> = (props) => {
  const {
    campaign,
    contacts,
    isLoading,
    pagination,
    refreshDisabled,
    resumableSubIds,
    searchTerm,
    selectedContactIds,
    selectedContactsById,
    selectionDisabledReason,
    subscriptionFilter,
    onClearSelection,
    onRefresh,
    onResume,
    onSearch,
    onSelectContact,
    onSubscribe,
    onSubscriptionFilterChange,
    onUnsubscribe,
  } = props
  const isMobile = useIsMobile()
  const selectedCount = selectedContactIds.length
  const emptyMessage = searchTerm ? `No results for "${searchTerm}".` : 'No contacts found.'
  const refreshLabel = 'Reload contacts'
  const searchLabel = 'Search contacts'

  if (isMobile) {
    return (
      <MobileSubscriptionsLayout
        actions={
          <ContactActions
            campaign={campaign}
            isLoading={isLoading}
            isMobile
            resumableSubIds={resumableSubIds}
            selectedContactIds={selectedContactIds}
            onResume={onResume}
            onSubscribe={onSubscribe}
            onUnsubscribe={onUnsubscribe}
          />
        }
        emptyMessage={emptyMessage}
        filter={<ResponsiveSubFilter subscriptionFilter={subscriptionFilter} onChange={onSubscriptionFilterChange} />}
        isLoading={isLoading}
        pageSize={contacts.length}
        pagination={pagination}
        refreshDisabled={refreshDisabled}
        refreshLabel={refreshLabel}
        searchLabel={searchLabel}
        searchTerm={searchTerm}
        selectedCount={selectedCount}
        onClearSelection={onClearSelection}
        onRefresh={onRefresh}
        onSearch={onSearch}
      >
        {contacts.map((contact) => (
          <MobileContactSubCard
            key={contact.id}
            contact={contact}
            isSelected={!!selectedContactsById[contact.id!]}
            selectionDisabledReason={selectionDisabledReason}
            onSelect={onSelectContact}
          />
        ))}
      </MobileSubscriptionsLayout>
    )
  }

  return (
    <div data-testid="desktop-subscriptions-table">
      <TableToolbar>
        <Box sx={{ display: 'flex', gap: 1, marginRight: 'auto' }}>
          <ContactActions
            campaign={campaign}
            isLoading={isLoading}
            isMobile={false}
            resumableSubIds={resumableSubIds}
            selectedContactIds={selectedContactIds}
            onResume={onResume}
            onSubscribe={onSubscribe}
            onUnsubscribe={onUnsubscribe}
          />
        </Box>
        <SearchBar
          namespace="campaign-manage-subscriptions"
          disabled={isLoading}
          label={searchLabel}
          searchTerm={searchTerm}
          onSubmit={onSearch}
        />
        <ResponsiveSubFilter subscriptionFilter={subscriptionFilter} onChange={onSubscriptionFilterChange} />
        <Tooltip title={refreshLabel}>
          <span>
            <IconButton aria-label={refreshLabel} disabled={refreshDisabled} onClick={onRefresh}>
              <Icon>refresh</Icon>
            </IconButton>
          </span>
        </Tooltip>
      </TableToolbar>
      <TableLayout loading={isLoading} pageSize={contacts.length} pagination={pagination} emptyMessage={emptyMessage}>
        <Table>
          <TableHead>
            <TableRow>
              <MUITableCell padding="checkbox">
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography color="textSecondary">{selectedCount} selected</Typography>
                  <Tooltip title={SELECT_CONTACTS_INFO_TOOLTIP}>
                    <Box component="span" sx={{ display: 'inline-flex' }} tabIndex={0}>
                      <Icon aria-label="Select contacts info" color="action" fontSize="small">
                        info_outline
                      </Icon>
                    </Box>
                  </Tooltip>
                  <Button disabled={selectedCount === 0} size="small" variant="text" onClick={onClearSelection}>
                    Clear
                  </Button>
                </Box>
              </MUITableCell>
              <TableCell index={0}>Name</TableCell>
              <TableCell index={1}>Phone number</TableCell>
              <TableCell index={2}>Subscription</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <ContactsTableRow
                key={contact.id}
                contact={contact}
                isLoading={isLoading}
                isSelected={!!selectedContactsById[contact.id!]}
                itemId={contact.id!}
                selectionDisabledReason={selectionDisabledReason}
                onSelect={onSelectContact}
              />
            ))}
          </TableBody>
        </Table>
        {isLoading && <Loader />}
      </TableLayout>
      <Divider />
    </div>
  )
}

const ResponsiveGroupSegmentSubTable: React.FC<ResponsiveGroupSegmentSubTableProps> = (props) => {
  const {
    campaign,
    groups,
    isLoading,
    pagination,
    processingGroupIds,
    refreshDisabled,
    root,
    searchTerm,
    selectedGroupIds,
    selectedGroupsById,
    selectionDisabledReason,
    timezone,
    onClearSelection,
    onRefresh,
    onSearch,
    onSelectGroup,
    onSubscribe,
    onUnsubscribe,
  } = props
  const isMobile = useIsMobile()
  const data = React.useMemo(
    () =>
      groups.reduce<Record<number, SchemaGroup>>((result, group) => {
        result[group.id] = group
        return result
      }, {}),
    [groups],
  )
  const specs = React.useMemo(() => getSpecs(timezone), [timezone])
  const isSelectionDisabled = !!selectionDisabledReason
  const selectedCount = selectedGroupIds.length
  const MobileSubCard = root === 'segments' ? MobileSegmentSubCard : MobileGroupSubCard
  const itemLabel = root === 'segments' ? 'segments' : 'groups'
  const emptyMessage = searchTerm ? `No results for "${searchTerm}".` : `No ${itemLabel} found.`
  const refreshLabel = `Reload ${itemLabel}`
  const searchLabel = `Search ${itemLabel}`

  if (isMobile) {
    return (
      <MobileSubscriptionsLayout
        actions={
          <GroupActions
            campaign={campaign}
            isLoading={isLoading}
            isMobile
            root={root}
            selectedGroupIds={selectedGroupIds}
            onSubscribe={onSubscribe}
            onUnsubscribe={onUnsubscribe}
          />
        }
        emptyMessage={emptyMessage}
        isLoading={isLoading}
        pageSize={groups.length}
        pagination={pagination}
        refreshDisabled={refreshDisabled}
        refreshLabel={refreshLabel}
        searchLabel={searchLabel}
        searchTerm={searchTerm}
        selectedCount={selectedCount}
        onClearSelection={onClearSelection}
        onRefresh={onRefresh}
        onSearch={onSearch}
      >
        {groups.map((group) => (
          <MobileSubCard
            key={group.id}
            group={group}
            isSelected={!!selectedGroupsById[group.id]}
            selectionDisabledReason={selectionDisabledReason}
            timezone={timezone}
            onSelect={onSelectGroup}
          />
        ))}
      </MobileSubscriptionsLayout>
    )
  }

  return (
    <div data-testid="desktop-subscriptions-table">
      <TableToolbar>
        <Box sx={{ display: 'flex', gap: 1, marginRight: 'auto' }}>
          <GroupActions
            campaign={campaign}
            isLoading={isLoading}
            isMobile={false}
            root={root}
            selectedGroupIds={selectedGroupIds}
            onSubscribe={onSubscribe}
            onUnsubscribe={onUnsubscribe}
          />
        </Box>
        <SearchBar
          namespace="campaign-manage-subscriptions"
          disabled={isLoading}
          label={searchLabel}
          searchTerm={searchTerm}
          onSubmit={onSearch}
        />
        <Tooltip title={refreshLabel}>
          <span>
            <IconButton aria-label={refreshLabel} disabled={refreshDisabled} onClick={onRefresh}>
              <Icon>refresh</Icon>
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ paddingRight: 1, width: 0 }} />
      </TableToolbar>
      <TableLayout loading={isLoading} pageSize={groups.length} pagination={pagination} emptyMessage={emptyMessage}>
        <Table>
          <TableHead>
            <TableRow>
              <MUITableCell padding="checkbox">
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography color="textSecondary">{selectedCount} selected</Typography>
                  <Tooltip title={SELECT_CONTACTS_INFO_TOOLTIP}>
                    <Box component="span" sx={{ display: 'inline-flex' }} tabIndex={0}>
                      <Icon aria-label="Select contacts info" color="action" fontSize="small">
                        info_outline
                      </Icon>
                    </Box>
                  </Tooltip>
                  <Button disabled={selectedCount === 0} size="small" variant="text" onClick={onClearSelection}>
                    Clear
                  </Button>
                </Box>
              </MUITableCell>
              {headers.map(({ title }, index) => (
                <TableCell key={title} index={index}>
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group) => {
              const isSelected = !!selectedGroupsById[group.id]

              return (
                <TableRow
                  key={group.id}
                  isLoading={processingGroupIds.includes(group.id)}
                  onClick={() => {
                    if (!isSelectionDisabled) {
                      onSelectGroup(group.id, !isSelected)
                    }
                  }}
                >
                  <MUITableCell padding="checkbox">
                    <Tooltip title={selectionDisabledReason}>
                      <span onClick={(event) => event.stopPropagation()}>
                        <MuiCheckbox
                          checked={isSelected}
                          disabled={isSelectionDisabled}
                          slotProps={{ input: { 'aria-label': `Select ${group.name}` } }}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => onSelectGroup(group.id, event.target.checked)}
                        />
                      </span>
                    </Tooltip>
                  </MUITableCell>
                  {headers.map(({ fields, icon }, index) => (
                    <StyledTableCell key={index} index={index}>
                      <IconText>
                        {icon && <Icon>{icon}</Icon>}
                        {fields.map((key, fieldIndex) => {
                          const field = pathOr('', [group.id, key], data)
                          return (
                            <FieldStatic
                              key={key}
                              name={key}
                              field={fieldIndex > 0 ? ` ${field}` : field}
                              specs={specs[key]}
                              title={field}
                            />
                          )
                        })}
                      </IconText>
                    </StyledTableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {isLoading && <Loader />}
      </TableLayout>
      <Divider />
    </div>
  )
}

const ResponsiveGroupSubTable: React.FC<ResponsiveGroupSubTableProps> = (props) => (
  <ResponsiveGroupSegmentSubTable {...props} root="groups" />
)

const ResponsiveSegmentSubTable: React.FC<ResponsiveGroupSubTableProps> = (props) => (
  <ResponsiveGroupSegmentSubTable {...props} root="segments" />
)

const headers: TableHeader[] = [
  {
    title: 'Name',
    fields: ['name'],
    icon: '',
  },
  {
    title: 'Created',
    fields: ['createdAt'],
    icon: '',
  },
  {
    title: 'Number of contacts',
    fields: ['numberOfContacts'],
    icon: '',
  },
]

const getSpecs = (timezone: string): TableSpecs => ({
  id: {
    type: 'hidden',
    display: 'hidden',
  },
  name: {
    type: 'text',
    display: 'text',
  },
  createdAt: {
    type: 'datetime',
    display: 'datetime',
    tz: timezone,
  },
  numberOfContacts: {
    type: 'text',
    display: 'text',
    icon: 'group',
  },
})

const StyledTableCell = styled(TableCell)(() => ({
  '& .MuiTypography-root': {
    maxWidth: '30vw',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}))

export { ResponsiveContactSubTable, ResponsiveGroupSubTable, ResponsiveSegmentSubTable }
